import * as React from "react";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import {
  addAccount,
  removeAccount,
  setActiveAccount,
  type MagentoAccount,
} from "../slices/accountsSlice";
import { clearDiffs } from "../slices/diffSlice";
import { clearProducts } from "../slices/productsSlice";
import { fetchProducts } from "../services/magento/adobeCommerce";

interface StoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialForm = {
  label: "",
  baseUrl: "",
  consumerKey: "",
  consumerSecret: "",
  accessToken: "",
  accessTokenSecret: "",
  storeCode: "",
};

const StoreModal: React.FC<StoreModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector((state) => state.accounts.accounts);
  const activeAccountId = useAppSelector((state) => state.accounts.activeAccountId);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);

  const activeAccount = useMemo(
    () => accounts.find((account) => account.id === activeAccountId) || null,
    [accounts, activeAccountId]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.label || !form.baseUrl || !form.consumerKey || !form.consumerSecret) return;
    if (!form.accessToken || !form.accessTokenSecret) return;

    setError(null);
    setIsValidating(true);

    const id = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `account-${Date.now()}`;

    const account: MagentoAccount = {
      id,
      label: form.label.trim(),
      baseUrl: form.baseUrl.trim(),
      consumerKey: form.consumerKey.trim(),
      consumerSecret: form.consumerSecret.trim(),
      accessToken: form.accessToken.trim(),
      accessTokenSecret: form.accessTokenSecret.trim(),
      storeCode: form.storeCode.trim() || undefined,
    };

    try {
      await fetchProducts(account, 1, 1);
    } catch (err: any) {
      setError("Failed to connect to Magento. Please check your credentials and URL.");
      setIsValidating(false);
      return;
    }

    dispatch(addAccount(account));
    dispatch(clearProducts());
    dispatch(clearDiffs());
    setForm(initialForm);
    setShowAddForm(false);
    setIsValidating(false);
  };

  const handleSwitch = (accountId: string) => {
    dispatch(setActiveAccount(accountId));
    dispatch(clearProducts());
    dispatch(clearDiffs());
    onClose();
  };

  const handleRemove = (accountId: string) => {
    dispatch(removeAccount(accountId));
    dispatch(clearProducts());
    dispatch(clearDiffs());
    setAccountToDelete(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">Manage Collections</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Connected Stores</h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                showAddForm ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
              }`}
              title={showAddForm ? "Cancel" : "Add Store"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showAddForm ? (
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                )}
              </svg>
            </button>
          </div>

          {!showAddForm ? (
            <div className="space-y-3">
              {accounts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-500">No stores connected yet.</p>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="mt-3 text-sm font-medium text-emerald-600 hover:text-emerald-700 underline"
                  >
                    Connect your first store
                  </button>
                </div>
              ) : (
                accounts.map((account) => (
                  <div
                    key={account.id}
                    className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                      activeAccount?.id === account.id ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex-1 min-w-0 pr-4">
                      <p className="truncate text-sm font-semibold text-slate-900">{account.label}</p>
                      <p className="truncate text-xs text-slate-500">{account.baseUrl}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {accountToDelete === account.id ? (
                        <>
                          <span className="text-xs font-semibold text-rose-600 mr-1">Sure?</span>
                          <button
                            onClick={() => handleRemove(account.id)}
                            className="rounded-md bg-rose-50 border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-700 shadow-sm hover:bg-rose-100 transition-colors"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setAccountToDelete(null)}
                            className="rounded-md bg-white border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <>
                          {activeAccount?.id === account.id ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-tight text-emerald-700">
                              Active
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSwitch(account.id)}
                              className="rounded-md bg-white border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
                            >
                              Use
                            </button>
                          )}
                          <button
                            onClick={() => setAccountToDelete(account.id)}
                            className="rounded-md p-1.5 text-rose-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                            title="Remove Store"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleAddAccount}>
              {error && (
                <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-600 ring-1 ring-rose-200">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Store Label</label>
                  <input
                    name="label"
                    value={form.label}
                    onChange={handleChange}
                    placeholder="e.g. My Magento Store"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Base URL</label>
                  <input
                    name="baseUrl"
                    value={form.baseUrl}
                    onChange={handleChange}
                    placeholder="https://store.example.com/rest"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Consumer Key</label>
                    <input
                      name="consumerKey"
                      value={form.consumerKey}
                      onChange={handleChange}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Consumer Secret</label>
                    <input
                      name="consumerSecret"
                      value={form.consumerSecret}
                      onChange={handleChange}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Access Token</label>
                    <input
                      name="accessToken"
                      value={form.accessToken}
                      onChange={handleChange}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Token Secret</label>
                    <input
                      name="accessTokenSecret"
                      value={form.accessTokenSecret}
                      onChange={handleChange}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="pt-2 text-xs text-slate-500">
                Need API keys? <a href="https://experienceleague.adobe.com/docs/commerce-admin/systems/integrations.html" target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700 underline">Learn how to generate an integration in Magento</a>.
              </div>
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isValidating}
                  className="flex-1 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
                >
                  {isValidating ? "Validating..." : "Save Connection"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreModal;
